import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const ResignationsPage = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [resignations, setResignations] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [form, setForm] = useState({
    empNo: "",
    reason: "",
    leavingDate: "",
    resignDate: "",
    announcedDate: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const employeeSearchRef = useRef(null);

  const buildHeaders = () => ({
    "user-id": localStorage.getItem("token"),
    "user-type": localStorage.getItem("userType"),
    "company-id": localStorage.getItem("companyId"),
  });

  useEffect(() => {
    fetchEmployees();
    fetchResignations();
    // eslint-disable-next-line
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/employees`, { headers: buildHeaders() });
      setEmployees(res.data);
    } catch (e) {
      setError("Failed to load employees");
    }
  };

  const fetchResignations = async () => {
    try {
      const res = await axios.get(`${API_URL}/resignations`, { headers: buildHeaders() });
      setResignations(res.data);
    } catch (e) {
      setError("Failed to load resignations");
    }
  };

  const employeeSuggestions = useMemo(() => {
    const raw = String(employeeSearch || "").trim().toLowerCase();
    if (!raw) return [];

    const digits = raw.replace(/\D/g, "");

    const matches = employees
      .map((e) => {
        const name = String(e.name || "").toLowerCase();
        const empNo = String(e.empNo || "").toLowerCase();
        const empDigits = empNo.replace(/\D/g, "");

        const nameMatch = raw.length >= 2 && name.includes(raw);
        const empNoMatch = empNo.includes(raw);
        const digitsMatch = digits.length > 0 && empDigits.endsWith(digits);

        if (!nameMatch && !empNoMatch && !digitsMatch) return null;

        let score = 0;
        if (name.startsWith(raw)) score += 3;
        if (nameMatch) score += 2;
        if (empDigits === digits && digits.length > 0) score += 4;
        if (digitsMatch) score += 3;
        if (empNoMatch) score += 1;

        return { employee: e, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map((x) => x.employee);

    return matches;
  }, [employees, employeeSearch]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      const el = employeeSearchRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setShowEmployeeDropdown(false);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (!form.empNo) {
        setError("Please select an employee");
        return;
      }
      const payload = { ...form };
      // ensure empty optional fields are sent as null
      if (!payload.announcedDate) payload.announcedDate = null;
      if (!payload.notes) payload.notes = null;
      await axios.post(`${API_URL}/resignations`, payload, { headers: buildHeaders() });
      setSuccess("Resignation recorded successfully.");
      setForm({ empNo: "", reason: "", leavingDate: "", resignDate: "", announcedDate: "", notes: "" });
      setEmployeeSearch("");
      setShowEmployeeDropdown(false);
      fetchResignations();
      fetchEmployees(); // list excludes resigned now
    } catch (e) {
      setError(e.response?.data?.error || "Failed to submit resignation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-900 to-blue-700 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Employee Resignations</h2>
            <button onClick={() => navigate(-1)} className="text-blue-600 hover:underline">← Back</button>
          </div>
          {error && <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-3">{error}</div>}
          {success && <div className="bg-green-100 text-green-700 px-4 py-2 rounded mb-3">{success}</div>}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2" ref={employeeSearchRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
              <div className="relative">
                <input
                  type="text"
                  value={employeeSearch}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEmployeeSearch(v);
                    setShowEmployeeDropdown(true);
                    setForm((prev) => ({ ...prev, empNo: "" }));
                  }}
                  onFocus={() => setShowEmployeeDropdown(true)}
                  placeholder="Type employee name or number (e.g., 0001)"
                  className="w-full border rounded px-3 py-2"
                  autoComplete="off"
                  required
                />

                {showEmployeeDropdown && employeeSuggestions.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border rounded shadow max-h-60 overflow-auto">
                    {employeeSuggestions.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => {
                          setForm((prev) => ({ ...prev, empNo: e.empNo }));
                          setEmployeeSearch(`${e.empNo} - ${e.name}`);
                          setShowEmployeeDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100"
                      >
                        <div className="font-medium">{e.name}</div>
                        <div className="text-sm text-gray-600">{e.empNo}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Leaving Reason</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Leaving Date</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2"
                value={form.leavingDate}
                onChange={(e) => setForm({ ...form, leavingDate: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Resign</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2"
                value={form.resignDate}
                onChange={(e) => setForm({ ...form, resignDate: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Announced Date</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2"
                value={form.announcedDate}
                onChange={(e) => setForm({ ...form, announcedDate: e.target.value })}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded font-semibold"
              >
                {loading ? "Submitting..." : "Submit Resignation"}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-xl font-bold mb-4">Resignations</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-max whitespace-nowrap">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="p-3">Emp No</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Reason</th>
                  <th className="p-3">Leaving</th>
                  <th className="p-3">Resign Date</th>
                  <th className="p-3">Announced</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {resignations.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-4 text-center text-gray-500">No resignations recorded</td>
                  </tr>
                ) : (
                  resignations.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-gray-50">
                      <td className="p-3">
                        <Link className="text-blue-600 underline" to={`/resignations/${r.empNo}`}>{r.empNo}</Link>
                      </td>
                      <td className="p-3">{r.name}</td>
                      <td className="p-3">{r.resign_reason}</td>
                      <td className="p-3">{r.leaving_date?.slice(0,10)}</td>
                      <td className="p-3">{r.resign_date?.slice(0,10)}</td>
                      <td className="p-3">{r.announced_date ? r.announced_date.slice(0,10) : '—'}</td>
                      <td className="p-3">
                        {r.last_restored_at ? `Restored (${String(r.last_restored_at).slice(0, 10)})` : 'Resigned'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResignationsPage;
